import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Loader2, Check, Clock, Mail, Gift as GiftIcon } from "lucide-react";

const GiftThanks = () => {
  const { cardId } = useParams();
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true, status: null, error: "" });
  const [tries, setTries] = useState(0);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const { data } = await api.get(`/gifts/order/${cardId}/status`);
        if (!alive) return;
        setState({ loading: false, status: data, error: "" });
        // Keep polling if still pending_payment (webhook race)
        if (data.status === "pending_payment" && tries < 10) {
          setTimeout(() => setTries((t) => t + 1), 2000);
        }
      } catch (err) {
        if (!alive) return;
        setState({ loading: false, status: null, error: err?.response?.data?.detail || "Could not load your order." });
      }
    };
    check();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, tries]);

  if (state.loading) {
    return (
      <div className="grain min-h-screen flex items-center justify-center text-[#a39797]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="grain min-h-screen flex items-center justify-center p-6">
        <div className="card-noir p-6 max-w-md">
          <div className="text-[#ff6b6b] text-sm">{state.error}</div>
          <Link to="/gifts" className="btn-ghost text-sm mt-4"><ArrowLeft size={14} /> try again</Link>
        </div>
      </div>
    );
  }

  const s = state.status;
  const pending = s.status === "pending_payment";
  const scheduled = s.status === "scheduled";
  const delivered = s.status === "delivered";

  return (
    <div className="grain min-h-screen" data-testid="gift-thanks-page">
      <div className="max-w-xl mx-auto px-6 py-12 text-center">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-10"
          data-testid="thanks-back">
          <ArrowLeft size={16} /> back to the file
        </Link>

        {pending ? (
          <>
            <Loader2 size={48} className="mx-auto mb-4 animate-spin text-[#d4af37]" />
            <h1 className="font-display text-3xl mb-2">Finalizing payment…</h1>
            <p className="text-[#a39797]">This should only take a few seconds.</p>
          </>
        ) : (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#8b0000]/20 mb-4">
              <Check size={32} className="text-[#4ade80]" />
            </div>
            <h1 className="font-display text-4xl mb-2">The gift is set.</h1>
            {scheduled ? (
              <>
                <p className="text-[#a39797] mb-6">
                  <Clock size={14} className="inline mr-1" />
                  {s.recipient_name} will get it on <b className="text-[#f4f0ec]">{s.deliver_at}</b> at 9:00 am.
                </p>
                <p className="text-xs text-[#a39797]">Need to fix the email or the date? Reply to the receipt we just sent you.</p>
              </>
            ) : delivered ? (
              <>
                <p className="text-[#a39797] mb-6">
                  <Mail size={14} className="inline mr-1" />
                  We just emailed {s.recipient_name}'s gift. Expect a slight delay if the inbox gets busy.
                </p>
                <p className="text-xs text-[#a39797]">
                  Code preview: <span className="font-mono tracking-wider">{s.code_preview}</span>
                </p>
              </>
            ) : (
              <p className="text-[#a39797] mb-6">We've recorded your order.</p>
            )}

            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/" className="btn-ghost text-sm">
                <ArrowLeft size={14} /> home
              </Link>
              <Link to="/gifts" className="btn-primary text-sm" data-testid="btn-send-another">
                <GiftIcon size={14} /> send another
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GiftThanks;
